import React, { useCallback, useEffect, useMemo } from 'react';
import { collection, query, where, orderBy } from 'firebase/firestore';
import { useFirestore, useFirestoreCollectionData } from 'reactfire';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { isNil, isEmpty, upperFirst } from 'lodash';
import { t } from 'i18next';
import RelativeTime from '@yaireo/relative-time';

import {
  Grid,
  Box,
  Flex,
  Avatar,
  Text,
  SkeletonText,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Image,
  Skeleton,
  Center,
} from '@chakra-ui/react';

import FontIcon from 'components/font-icon';

import { ReactComponent as LocationIcon } from '../../assets/icon/location.svg';
import { ReactComponent as GlobeIcon } from '../../assets/icon/globe.svg';
/**
 * Somehow cannot import shopping cart icon as component
 */
import ShoppingCartIcon from '../../assets/icon/shopping-cart.svg';

import { actions } from 'slices/app.slice';
import { actions as landingActions } from 'slices/landing.slice';

import { collections, landingPageTheme } from 'utils/const';
import generatePlaceholderArray from 'utils/generate-placeholder-array';
import getFallbackProductUrl from 'utils/get-fallback-product-url';
import convertPrefToTagFormat from 'utils/convert-pref-to-tag-format';
import getFirstNameAndLastName from 'utils/get-first-and-last-name';

const isBlank = (val) => isNil(val) || isEmpty(val);

const getIgUrl = (handle) => `https://instagram.com/${handle}`;
const getTiktokUrl = (handle) => `https://tiktok.com/@${handle}`;
const getYtUrl = (handle) => `https://youtube.com/user/${handle}`;

const Landing = () => {
  const dispatch = useDispatch();

  const { username } = useParams();

  const { me, loggedIn, checked } = useSelector((state) => state.app);

  useEffect(() => {
    if (!me?.id && !loggedIn && checked) {
      dispatch(actions.loginAnonymously(true));
    }
  }, [me?.id, loggedIn, checked, dispatch]);

  useEffect(() => {
    if (!isBlank(username)) {
      dispatch(landingActions.fetchCreatorProfile(username));
    }
  }, [username, dispatch]);

  const collectionRef = collection(
    useFirestore(),
    collections.creatorRecommendations
  );

  const { fetchProfileStatus, creatorProfile } = useSelector(
    (state) => state.landing
  );

  // TODO: Allow this query to be called conditionally (when the `creatorProfile` has been resolved).
  // We have to extract this to another component if were to stick with using `reactfire`, otherwise, we can
  // create a hook that handles this data fetching.
  // https://github.com/FirebaseExtended/reactfire/issues/463
  const recommendationsDataQuery = query(
    collectionRef,
    where('createdBy', '==', creatorProfile?.email ?? ''),
    orderBy('createdAt', 'desc')
  );

  const { status: fetchRecommendationsStatus, data: recommendationsData } =
    useFirestoreCollectionData(recommendationsDataQuery, {
      initialData: [],
      suspense: true,
    });

  const isLoading = useMemo(
    () =>
      fetchRecommendationsStatus === 'loading' ||
      fetchProfileStatus === 'loading' ||
      isBlank(recommendationsData),
    [fetchProfileStatus, fetchRecommendationsStatus, recommendationsData]
  );

  const recommendations = useMemo(
    () =>
      isLoading ? generatePlaceholderArray('name', 12) : recommendationsData,
    [isLoading, recommendationsData]
  );

  const getTagsFromCurrentProduct = (productId) => {
    if (isLoading) {
      return generatePlaceholderArray('name', 2);
    }

    return recommendations?.find((rec) => rec.productId === productId)?.tags;
  };

  const getProductUrl = (product) => {
    return (
      product.url ??
      getFallbackProductUrl(
        recommendations.find(({ productId }) => productId === product.productId)
          ?.retailer,
        product.productId
      )
    );
  };

  const isDisplayedThirdProfileSection =
    !isBlank(creatorProfile?.location) ||
    !isBlank(creatorProfile?.website) ||
    !isBlank(creatorProfile?.tiktok) ||
    !isBlank(creatorProfile?.instagram) ||
    !isBlank(creatorProfile?.youtube);

  const getRelativeTime = useCallback(
    (productId) => {
      if (!isLoading) {
        const relativeTime = new RelativeTime({ locale: 'en' });
        const timestampInSeconds = recommendations?.find(
          (rec) => rec.productId === productId
        )?.createdAt.seconds;
        return relativeTime.from(new Date(timestampInSeconds * 1000));
      }
      return '';
    },
    [isLoading, recommendations]
  );

  const getProductNote = useCallback(
    (productId) => {
      if (!isLoading) {
        return (
          recommendations?.find((rec) => rec.productId === productId)?.note ??
          ''
        );
      }
      return '';
    },
    [isLoading, recommendations]
  );

  if (fetchProfileStatus === 'success' && isNil(creatorProfile)) {
    return (
      <Flex
        alignItems="center"
        height="100vh"
        width="100vw"
        direction="column"
        pt={8}
      >
        <Text fontSize="2xl">
          No user with username
          <Text fontWeight="bold" as="span">
            {' '}
            {username}{' '}
          </Text>
          is found
        </Text>
        <Text fontSize="xl" color="gray">
          Try searching for another user.
        </Text>
      </Flex>
    );
  }

  return (
    <Grid paddingX="5" width="full">
      <Box boxSize="8" />
      <Flex alignItems="center" justifyContent="space-between" direction="row">
        <Avatar
          boxShadow="inset 60px 60px rgb(33 31 20 / 25%)"
          borderColor="orange.500"
          borderWidth="thick"
          borderRadius={['36px', '50px', '50px']}
          height="28"
          width="28"
          showBorder
          src={creatorProfile?.avatarUrl}
        />
        {/* Start - Follow Button */}
        {/* <Button
          title="Follow"
          fontSize="2xl"
          height="16"
          width="36"
          colorScheme="green"
          variant="rounded"
        >
          Follow
        </Button> */}
        {/* End - Follow Button */}
      </Flex>
      <Box boxSize="2.5" />
      {fetchProfileStatus === 'loading' ? (
        <SkeletonText noOfLines={1} width="20" />
      ) : (
        <Text
          color={landingPageTheme.newBlack}
          fontWeight="semibold"
          fontSize="2xl"
        >
          {upperFirst(creatorProfile?.name)}
        </Text>
      )}
      {/* Start - new profile section */}
      {creatorProfile?.username && (
        <>
          <Box boxSize="1" />
          <Text color={landingPageTheme.newGray2} fontSize="xl">
            @{creatorProfile.username}
          </Text>
        </>
      )}
      {creatorProfile?.bio && (
        <>
          <Box boxSize="4" />
          <Text color={landingPageTheme.newGray2} fontSize="lg">
            {creatorProfile.bio}
          </Text>
        </>
      )}
      {isDisplayedThirdProfileSection && (
        <>
          <Box boxSize="4" />
          <Flex direction="row" columnGap="6" alignItems="center">
            <Skeleton
              isLoaded={fetchProfileStatus === 'success'}
              display={
                fetchProfileStatus === 'success' &&
                isNil(creatorProfile.location)
                  ? 'none'
                  : 'unset'
              }
            >
              <Flex gridGap="2" alignItems="center" direction="row">
                <LocationIcon />
                <Text
                  color={landingPageTheme.newGray3}
                  fontSize="lg"
                  noOfLines={1}
                >
                  {creatorProfile?.location}
                </Text>
              </Flex>
            </Skeleton>
            <Skeleton
              isLoaded={fetchProfileStatus === 'success'}
              maxW="50%"
              display={
                fetchProfileStatus === 'success' &&
                isNil(creatorProfile.website)
                  ? 'none'
                  : 'unset'
              }
            >
              <Flex gridGap="0.5" alignItems="center" direction="row">
                <GlobeIcon />
                <Text
                  color={landingPageTheme.newGray3}
                  fontSize="lg"
                  noOfLines={1}
                  as="a"
                  href={creatorProfile?.website}
                  target="_blank"
                  rel="noreferrer"
                  _active={{ color: landingPageTheme.newGray3 }}
                  _hover={{ color: landingPageTheme.newGray3 }}
                >
                  {/* https://stackoverflow.com/a/41942787 */}
                  {
                    creatorProfile?.website
                      ?.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '')
                      .split('/')[0]
                  }
                </Text>
              </Flex>
            </Skeleton>
            <Flex
              gridGap="5"
              alignItems="center"
              justifyContent="flex-start"
              direction="row"
            >
              {creatorProfile?.tiktok && (
                <Box fontSize="lg">
                  <a
                    href={getTiktokUrl(creatorProfile.tiktok)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <FontIcon
                      type="fab"
                      name="tiktok"
                      color={landingPageTheme.newGray3}
                    />
                  </a>
                </Box>
              )}
              {creatorProfile?.instagram && (
                <Box fontSize="lg">
                  <a
                    href={getIgUrl(creatorProfile.instagram)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <FontIcon
                      type="fab"
                      name="instagram"
                      color={landingPageTheme.newGray3}
                    />
                  </a>
                </Box>
              )}
              {creatorProfile?.youtube && (
                <Box fontSize="lg">
                  <a
                    href={getYtUrl(creatorProfile.youtube)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <FontIcon
                      type="fab"
                      name="youtube"
                      color={landingPageTheme.newGray3}
                    />
                  </a>
                </Box>
              )}
            </Flex>
          </Flex>
        </>
      )}
      {/* End - new profile section */}
      <Box boxSize="7" />
      <Tabs>
        <TabList border="none">
          <Tab
            _selected={{
              color: landingPageTheme.newBlack2,
              fontWeight: 'semibold',
              borderWidth: '1px',
              borderBottomColor: landingPageTheme.lightTeal,
            }}
            fontSize="lg"
          >
            {t('landing/products-tab')}
          </Tab>
          <Tab
            _selected={{
              color: landingPageTheme.newBlack2,
              fontWeight: 'semibold',
              borderWidth: '1px',
              borderBottomColor: landingPageTheme.lightTeal,
            }}
            fontSize="lg"
          >
            {t('landing/activities-tab')}
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel padding="0">
            {!isLoading ? (
              <>
                <Box boxSize="6" />
                <Text color={landingPageTheme.newBlack} fontSize="xl">
                  {t('landing/top-recommended-products', {
                    name: upperFirst(
                      getFirstNameAndLastName(creatorProfile?.name).firstName
                    ),
                  })}
                </Text>
              </>
            ) : null}
            <Grid
              gridGap="2rem"
              paddingTop="4"
              paddingBottom="8"
              templateColumns="repeat(auto-fit, minmax(12rem, 1fr))"
              width="full"
            >
              {recommendations.map((recommendation) => (
                <Flex
                  key={
                    isLoading
                      ? `product ${recommendation.name}`
                      : recommendation.productId
                  }
                  direction="column"
                >
                  <Box
                    position="relative"
                    width="fit-content"
                    onClick={() =>
                      window.open(getProductUrl(recommendation), '_blank')
                    }
                    _hover={{ cursor: 'pointer' }}
                  >
                    <Skeleton
                      isLoaded={!isLoading}
                      borderRadius="xl"
                      height={
                        isLoading
                          ? ['16rem', '16rem', '14rem', '14rem', '13rem']
                          : null
                      }
                      width={
                        isLoading
                          ? ['16rem', '16rem', '14rem', '14rem', '13rem']
                          : null
                      }
                    >
                      <Box
                        height={['16rem', '16rem', '14rem', '14rem', '13rem']}
                        width={['16rem', '16rem', '14rem', '14rem', '13rem']}
                      >
                        <Image
                          borderColor="borderColor"
                          borderStyle="solid"
                          borderWidth="1px"
                          borderRadius="xl"
                          width="full"
                          height="full"
                          alt="Recommendation image"
                          src={recommendation.productImage}
                        />
                      </Box>
                      {/* Need to use flex or else the icon failed to show up */}
                      <Flex
                        bgColor={landingPageTheme.newGray4}
                        right="2"
                        top="2"
                        position="absolute"
                        backdropFilter="blur(6px)"
                        borderRadius="lg"
                        alignItems="center"
                        justifyContent="center"
                        height="10"
                        width="10"
                      >
                        <Box width="fit-content">
                          <Image
                            src={ShoppingCartIcon}
                            zIndex="2"
                            alt="Cart icon"
                          />
                        </Box>
                      </Flex>
                    </Skeleton>
                  </Box>
                  <Box boxSize="2" />
                  {isLoading ? (
                    <SkeletonText noOfLines={1} />
                  ) : (
                    <Text
                      color={landingPageTheme.newBlack3}
                      noOfLines="2"
                      fontWeight="semibold"
                      fontSize="xl"
                      onClick={() =>
                        window.open(getProductUrl(recommendation), '_blank')
                      }
                      _hover={{ cursor: 'pointer' }}
                    >
                      {recommendation.productName}
                    </Text>
                  )}
                  <Box boxSize="2" />
                  <Flex gridGap="4" wrap="wrap" direction="row">
                    {!isBlank(
                      getTagsFromCurrentProduct(recommendation.productId)
                    ) &&
                      getTagsFromCurrentProduct(recommendation.productId).map(
                        (tag) => (
                          <React.Fragment key={tag.name}>
                            {isLoading ? (
                              <SkeletonText noOfLines={1} width="10" />
                            ) : (
                              <Box
                                key={tag.name}
                                py="px"
                                px="2"
                                backgroundColor={landingPageTheme.newGray}
                                borderRadius="xl"
                              >
                                <Text
                                  color={landingPageTheme.newBlue}
                                  fontWeight="semibold"
                                  fontSize="xs"
                                >
                                  {convertPrefToTagFormat(tag)}
                                </Text>
                              </Box>
                            )}
                          </React.Fragment>
                        )
                      )}
                  </Flex>
                </Flex>
              ))}
            </Grid>
          </TabPanel>
          <TabPanel padding="0">
            {!isLoading ? (
              <>
                <Box boxSize="6" />
                <Text color={landingPageTheme.newBlack} fontSize="xl">
                  {t('landing/activities-header', {
                    name: upperFirst(
                      getFirstNameAndLastName(creatorProfile?.name).firstName
                    ),
                  })}
                </Text>
              </>
            ) : null}
            <Grid gridGap="6" paddingTop="4" paddingBottom="8">
              {recommendations.map((recommendation) => (
                <Flex
                  key={
                    isLoading
                      ? `activity ${recommendation.name}`
                      : recommendation.productId
                  }
                  direction="column"
                  maxWidth={[
                    '92.8vw',
                    '93.8vw',
                    '94.8vw',
                    '95.8vw',
                    '96.8vw',
                    '97.8vw',
                  ]}
                >
                  <Box position="relative">
                    <Skeleton isLoaded={!isLoading} borderRadius="xl">
                      <Box
                        width="full"
                        borderRadius="xl"
                        backgroundColor="#FAFAFA"
                      >
                        <Flex
                          justifyContent="space-between"
                          alignItems="center"
                          paddingX="4"
                        >
                          <Flex>
                            <Avatar
                              boxShadow="inset 60px 60px rgb(33 31 20 / 25%)"
                              borderColor="orange.500"
                              borderWidth="thick"
                              borderRadius={['24px', '28px', '52px']}
                              height="16"
                              width="16"
                              showBorder
                              src={creatorProfile?.avatarUrl}
                            />
                            <Box boxSize="16px" />
                            <Flex direction="column" justifyContent="center">
                              <Flex>
                                <Text fontWeight="medium" fontSize="md">
                                  {upperFirst(creatorProfile?.name)}
                                </Text>
                                <Box boxSize="2" />
                                <Center>
                                  <Box
                                    bgColor="#D9D9D9"
                                    boxSize="2"
                                    borderRadius="full"
                                  />
                                </Center>
                                <Box boxSize="2" />
                                <Text
                                  fontWeight="medium"
                                  fontSize="md"
                                  color={landingPageTheme.newGray2}
                                >
                                  {getRelativeTime(recommendation.productId)}
                                </Text>
                              </Flex>
                              <Text
                                fontWeight="medium"
                                color={landingPageTheme.newGray2}
                                fontSize="xs"
                              >
                                @{creatorProfile.username}
                              </Text>
                            </Flex>
                          </Flex>
                          <Box
                            height="24"
                            width="24"
                            borderRadius="xl"
                            onClick={() =>
                              window.open(
                                getProductUrl(recommendation),
                                '_blank'
                              )
                            }
                            _hover={{ cursor: 'pointer' }}
                          >
                            <Image
                              height="full"
                              width="full"
                              paddingY="4"
                              alt="Recommendation image"
                              src={recommendation.productImage}
                            />
                          </Box>
                        </Flex>
                        <Box paddingX="4" paddingBottom="4">
                          <Box boxSize="2" />
                          <Text
                            color={landingPageTheme.newBlack3}
                            noOfLines="2"
                            fontWeight="600"
                            fontSize="md"
                            onClick={() =>
                              window.open(
                                getProductUrl(recommendation),
                                '_blank'
                              )
                            }
                            _hover={{ cursor: 'pointer' }}
                          >
                            {recommendation.productName}
                          </Text>
                          <Box boxSize="2" />
                          <Text color="#777777" fontSize="md">
                            {getProductNote(recommendation.productId)}
                          </Text>
                          <Box boxSize="2" />
                          {!isBlank(
                            getTagsFromCurrentProduct(recommendation.productId)
                          ) ? (
                            <Flex gridGap="4" wrap="wrap" direction="row">
                              {getTagsFromCurrentProduct(
                                recommendation.productId
                              ).map((tag) => (
                                <React.Fragment key={tag.name}>
                                  {isLoading ? (
                                    <SkeletonText noOfLines={1} width="10" />
                                  ) : (
                                    <Box
                                      key={tag.name}
                                      py="px"
                                      px="2"
                                      backgroundColor={landingPageTheme.newGray}
                                      borderRadius="xl"
                                      width="fit-content"
                                    >
                                      <Text
                                        color={landingPageTheme.newBlue}
                                        fontWeight="semibold"
                                        fontSize="xs"
                                      >
                                        {convertPrefToTagFormat(tag)}
                                      </Text>
                                    </Box>
                                  )}
                                </React.Fragment>
                              ))}
                            </Flex>
                          ) : null}
                        </Box>
                      </Box>
                    </Skeleton>
                  </Box>
                </Flex>
              ))}
            </Grid>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Grid>
  );
};

export default Landing;
